import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { updateHomelessInfoField } from 'redux/actions/homeless';
import { Homeless } from 'types';
import { unassignGiveCard } from 'api/givecard';
import { unassignPexCard } from 'api/pex';

interface UnassignCardState {
    isLoading: boolean;
    isComplete: boolean;
    error: string | null;
}

const useMemberUnassignDebit = (homeless: Homeless) => {
    const dispatch = useDispatch();

    const [state, setState] = useState<UnassignCardState>({
        isLoading: false,
        isComplete: false,
        error: null
    });

    const reset = useCallback(() => {
        setState({
            isLoading: false,
            isComplete: false,
            error: null
        });
    }, []);

    const execute = useCallback(async () => {
        if (state.isComplete) {
            reset();
            return;
        }

        setState(prev => ({
            ...prev,
            isLoading: true,
            error: null
        }));

        try {
            const cardType = homeless.card?.card_type?.toLowerCase();
            if (!cardType) {
                throw new Error('No card type specified');
            }

            let response;
            switch (cardType) {
                case 'pex':
                    response = await unassignPexCard(homeless.id);
                    break;
                case 'givecard':
                case 'virtual-givecard':
                    response = await unassignGiveCard(homeless.id);
                    break;
                default:
                    throw new Error('Invalid card type');
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData?.message || 'Failed to unassign debit card');
            }

            setState({
                isLoading: false,
                isComplete: true,
                error: null
            });

            dispatch(updateHomelessInfoField('card', ''));
            setTimeout(reset, 3000);

        } catch (error) {
            setState({
                isLoading: false,
                isComplete: false,
                error: error instanceof Error ? error.message : 'An unknown error occurred'
            });
        }
    }, [homeless.id, homeless.card?.card_type, dispatch, state.isComplete, reset]);

    return {
        execute,
        ...state
    };
};

export default useMemberUnassignDebit; 